import React, { useMemo } from "react";
import { Box, Heading, Stat, StatLabel, StatNumber, StatGroup, Table, Thead, Tbody, Tr, Th, Td, Badge, useColorModeValue } from "@chakra-ui/react";

function parseSarif(json) {
  if (!json) return { total: 0, severityCount: { error: 0, warning: 0, note: 0, none: 0 }, issues: [], grouped: {} };
  const runs = Array.isArray(json.runs) ? json.runs : [json.runs];
  let total = 0;
  let severityCount = { error: 0, warning: 0, note: 0, none: 0 };
  let grouped = {};
  runs.forEach(run => {
    const toolName = run?.tool?.driver?.name || 'Unknown Tool';
    const results = Array.isArray(run.results) ? run.results : [];
    total += results.length;
    results.forEach(r => {
      const level = r.level || 'warning';
      if (severityCount[level] !== undefined) severityCount[level]++;
      else severityCount[level] = 1;
    });
    grouped[toolName] = grouped[toolName] || [];
    results.forEach(r => {
      grouped[toolName].push({
        ruleId: r.ruleId,
        message: r.message && (r.message.text || r.message.markdown || ""),
        level: r.level || "warning"
      });
    });
  });
  // Flat issues for summary
  const issues = Object.values(grouped).flat();
  return { total, severityCount, issues, grouped };
}

const levelColor = {
  error: "red",
  warning: "yellow",
  note: "blue",
  none: "gray"
};

export default function SarifWidget({ sarifJson, hideTitle }) {
  const { total, severityCount, grouped } = useMemo(() => parseSarif(sarifJson), [sarifJson]);
  const boxBg = useColorModeValue('white', 'gray.800');
  // Collect tool names and versions
  const toolNames = useMemo(() => {
    const names = [];
    if (sarifJson && sarifJson.runs) {
      const runs = Array.isArray(sarifJson.runs) ? sarifJson.runs : [sarifJson.runs];
      runs.forEach(run => {
        const name = run?.tool?.driver?.name || 'Unknown Tool';
        const version = run?.tool?.driver?.version || '';
        names.push(version ? `${name} v${version}` : name);
      });
    }
    return Array.from(new Set(names));
  }, [sarifJson]);
  return (
    <Box p={4} borderWidth={1} borderRadius="lg" boxShadow="md" bg={boxBg}>
      <StatGroup mb={4}>
        <Stat>
          <StatLabel>Total Findings</StatLabel>
          <StatNumber>{total}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Dangerous</StatLabel>
          <StatNumber color="red.400">{severityCount.error}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Warnings</StatLabel>
          <StatNumber color="yellow.400">{severityCount.warning}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Notes</StatLabel>
          <StatNumber color="blue.400">{severityCount.note}</StatNumber>
        </Stat>
      </StatGroup>
      {/* Grouped by tool */}
      {Object.entries(grouped).map(([tool, issues]) => (
        issues.length > 0 && (
          <Box key={tool} mb={4}>
            <Heading size="sm" mb={2}>{tool}</Heading>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Rule</Th>
                  <Th>Severity</Th>
                  <Th>Description</Th>
                </Tr>
              </Thead>
              <Tbody>
                {issues.map((issue, i) => (
                  <Tr key={i}>
                    <Td>{issue.ruleId}</Td>
                    <Td><Badge colorScheme={levelColor[issue.level] || "gray"}>{issue.level}</Badge></Td>
                    <Td>{issue.message}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )
      ))}
      {/* Tool name(s) footer */}
      {toolNames.length > 0 && (
        <Box mt={4} textAlign="right" fontSize="xs" textTransform="uppercase" color="gray.400" letterSpacing="wider">
          {toolNames.join(', ')}
        </Box>
      )}
    </Box>
  );
} 